import { useEffect, useMemo, useState } from "react";
import { useBoundaries } from "../../../context/boundary";
import { useMap } from "../../../context/map";
import { useTheme } from "../../../context/theme";
import VectorTile from "../../reactOL/layers/VectorTile";
import { useMileageStyle } from "../useMileageStyle";
import { State, VectorTile as VectorTileSource } from "../../reactOL/source";
import MVT from "ol/format/MVT";
import { createXYZ } from "ol/tilegrid";
import VectorTileLayer from "ol/layer/VectorTile";

interface BoundaryVectorTileLayerProps {
  year: number;
  setLoadingState: (s: State) => void;
}

const BoundaryVectorTileLayer = ({
  year,
  setLoadingState,
}: BoundaryVectorTileLayerProps) => {
  const { mileageTheme, opacity } = useTheme();
  const { map } = useMap();
  const { selectedBoundary } = useBoundaries();

  const view = useMemo(() => map?.getView(), [map]);

  const style = useMileageStyle(
    mileageTheme,
    year,
    opacity,
    selectedBoundary?.id,
  );

  const tileGrid = useMemo(
    () =>
      createXYZ({
        extent: view?.getProjection().getExtent(),
        tileSize: 256,
        maxZoom: 15,
        minZoom: 0,
      }),
    [view],
  );

  const [boundarySource] = useState(() =>
    VectorTileSource({
      url: `${process.env.REACT_APP_TILE_URL}/data/tiles/{z}/{x}/{y}.mvt`,
      onStateChange: (state: State) => setLoadingState(state),
      authorizationToken: ``,
      projection: view?.getProjection(),
      format: new MVT(),
      tileGrid,
    }),
  );

  useEffect(() => {
    const o = selectedBoundary ? 1 : opacity;
    map?.getLayers().forEach((layer) => {
      if (layer instanceof VectorTileLayer) {
        layer.setOpacity(o);
      }
    });
  }, [selectedBoundary, opacity, map]);

  return (
    <VectorTile
      source={boundarySource}
      style={style}
      zIndex={50}
      visible={true}
    />
  );
};

export default BoundaryVectorTileLayer;
